import { useEffect, useRef } from "react";

/**
 * Sets up an IntersectionObserver to reveal elements with the `reveal` class
 * when they scroll into view. Elements already above the viewport (scrolled
 * past before the observer was created) are shown immediately.
 *
 * Needed for lazy-loaded sections whose reveal elements miss the homepage's
 * global observer, which runs on mount before the section exists in the DOM.
 *
 * @param {boolean} active - Whether to set up the observer (e.g. !loading)
 * @returns {React.RefObject} Attach to the container holding .reveal elements
 */
export function useReveal(active = true) {
  const ref = useRef(null);

  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;
    const els = container.querySelectorAll(".reveal:not(.is-visible)");
    if (els.length === 0) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    els.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0) {
        // Already scrolled past — show immediately
        el.classList.add("is-visible");
      } else {
        io.observe(el);
      }
    });

    return () => io.disconnect();
  }, [active]);

  return ref;
}